<template>
    <div class="flex items-center gap-2">
        <!-- Edit Mode: Show InputGroup with Save/Cancel buttons -->
        <InputGroup>
            <FloatLabel class="w-full" variant="on">
                <InputNumber 
                    v-model="localValue"
                    :readonly="isReadonly"
                    :disabled="isSaving"
                    :min="min"
                    :max="max"
                    :step="step"
                    :minFractionDigits="minFractionDigits"
                    :maxFractionDigits="maxFractionDigits"
                    :class="inputClass"
                    @keyup.enter="handleSave"
                    @keyup.escape="handleCancel"
                />
                <label>{{ label }}</label>
            </FloatLabel>
            <template v-if="!isReadonly">
                <InputGroupAddon>
                    <i 
                        v-if="!isSaving"
                        class="pi pi-check cursor-pointer text-green-600 hover:text-green-700"
                        :title="saveTooltip"
                        @click="handleSave"
                    ></i>
                    <i 
                        v-else
                        class="pi pi-spin pi-spinner"
                    ></i>
                </InputGroupAddon>
                <InputGroupAddon>
                    <i 
                        class="pi pi-times cursor-pointer text-red-600 hover:text-red-700"
                        :class="{ 'opacity-50 cursor-not-allowed': isSaving }"
                        :title="cancelTooltip"
                        @click="!isSaving && handleCancel()"
                    ></i>
                </InputGroupAddon>
            </template>
            <template v-else>
                <InputGroupAddon @click="enableEdit" v-if="canEdit">
                    <i 
                        class="pi pi-pencil cursor-pointer text-gray-600 hover:text-blue-600"
                        :title="editTooltip"
                    ></i>
                </InputGroupAddon>
            </template>
        </InputGroup>
    </div>
</template>

<script>
import axios from 'axios';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import FloatLabel from 'primevue/floatlabel';

export default {
    name: 'UnlockAndSaveInput',
    components: {
        InputNumber,
        InputText,
        InputGroup,
        InputGroupAddon,
        FloatLabel
    },
    props: {
        modelValue: {
            type: [String, Number],
            default: ''
        },
        saveUrl: {
            type: String,
            required: true
        },
        saveKey: {
            type: String,
            default: 'value'
        },
        type: {
            type: String,
            default: 'text'
        },
        label: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: ''
        },
        displayPrefix: {
            type: String,
            default: ''
        },
        displaySuffix: {
            type: String,
            default: ''
        },
        inputClass: {
            type: String,
            default: ''
        },
        editTooltip: {
            type: String,
            default: 'Edit'
        },
        saveTooltip: {
            type: String,
            default: 'Save'
        },
        cancelTooltip: {
            type: String,
            default: 'Cancel'
        },
        additionalData: {
            type: Object,
            default: () => ({})
        },
        canEdit: {
            type: Boolean,
            default: false
        },
        // InputNumber specific props
        min: {
            type: Number,
            default: undefined
        },
        max: {
            type: Number,
            default: undefined
        },
        step: {
            type: Number,
            default: 1
        },
        minFractionDigits: {
            type: Number,
            default: 0
        },
        maxFractionDigits: {
            type: Number,
            default: 0
        },
        startInEditMode: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:modelValue', 'value_saved', 'save-error', 'cancel'],
    data() {
        return {
            localValue: this.modelValue,
            originalValue: this.modelValue,
            isReadonly: !this.startInEditMode,
            isSaving: false
        };
    },
    computed: {
        displayValue() {
            return `${this.displayPrefix}${this.localValue}${this.displaySuffix}`;
        }
    },
    watch: {
        modelValue(newValue) {
            this.localValue = newValue;
            if (this.isReadonly) {
                this.originalValue = newValue;
            }
        }
    },
    methods: {
        enableEdit() {
            this.isReadonly = false;
            this.originalValue = this.localValue;
        },
        async handleSave() {
            if (this.isSaving) return;

            this.isSaving = true;

            try {
                const payload = {
                    [this.saveKey]: this.localValue,
                    ...this.additionalData
                };

                const response = await axios.post(this.saveUrl, payload);

                // Update the original value
                let valueSaved;
                if(response.data.success){
                    this.originalValue = this.localValue;
                    valueSaved = this.localValue
                   
                } else {
                    // Revert to original value if save failed
                    this.localValue = this.originalValue;
                    valueSaved = this.originalValue
                }
                this.$emit('update:modelValue', this.originalValue);
                
                // Emit the saved event with response data so parent can handle error message
                this.$emit('value_saved', {
                    value: valueSaved,
                    response: response.data
                });

                // Reset to readonly mode
                this.isReadonly = true;
            } catch (error) {
                console.error('Error saving value:', error);
                this.$emit('save-error', error);
            } finally {
                this.isSaving = false;
            }
        },
        handleCancel() {
            // Revert to original value
            this.localValue = this.originalValue;
            this.$emit('update:modelValue', this.originalValue);
            
            // Reset to readonly mode
            this.isReadonly = true;
            
            // Emit cancel event
            this.$emit('cancel');
        }
    }
};
</script>

<style scoped>
/* Additional custom styles can be added here if needed */
:deep(input[readonly]) {
    background-color: #f9f9f9!important;
    color: #6c757d!important;
}
:deep(.p-inputgroupaddon){
    cursor:pointer;
}
</style>
